/**
 * Gate in front of the executive one-pager: name, work email, health system.
 * Posts the ROI inputs with the lead so the PDF arrives personalised.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, FileDown, CheckCircle2, ExternalLink } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { API } from "@/App";

const ROLES = ["CFO / Finance", "VP Supply Chain", "Materials Management",
  "Rebate / Sourcing Analyst", "IT Leader", "Other"];

const BLANK = { name: "", email: "", organization: "", title: ROLES[0], phone: "" };

const field = `w-full rounded-xl border border-slate-300 bg-slate-50 px-4 py-3 text-[15px]
  text-[#0b2545] placeholder:text-slate-400 outline-none transition-colors
  focus:border-sky-500 focus:ring-2 focus:ring-cyan-400/20`;

export const HartOnePagerDialog = ({ open, onOpenChange, roiInputs }) => {
  const [form, setForm] = useState(BLANK);
  const [sending, setSending] = useState(false);
  const [link, setLink] = useState(null);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) return toast.error("Tell us who you are");
    if (!form.email.includes("@")) return toast.error("A work email, please");
    if (form.organization.trim().length < 2) return toast.error("Which health system?");
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/hart/one-pager`, { ...form, roi: roiInputs });
      const url = `${API}/hart/one-pager/${data.token}/pdf`;
      setLink(url);
      window.open(url, "_blank", "noopener");
      toast.success("One-pager ready", { description: `Also emailed to ${data.emailed_to}.` });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not build that — call (281) 456-3458");
    } finally {
      setSending(false);
    }
  };

  const close = (v) => {
    onOpenChange(v);
    if (!v) { setLink(null); setForm(BLANK); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-lg bg-white" data-testid="onepager-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black text-[#0a1c3c] tracking-tight">
            {link ? "Your one-pager is ready" : "Executive one-pager"}
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            {link
              ? "A copy is on its way to your inbox — forward it to whoever signs."
              : "One page, built on the numbers you just entered. Branded, CFO-ready, no deck."}
          </DialogDescription>
        </DialogHeader>

        {link ? (
          <div className="pt-1" data-testid="onepager-success">
            <div className="rounded-2xl border border-emerald-300 bg-emerald-50 p-5 text-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-700 mx-auto mb-2" />
              <p className="text-sm text-emerald-900 font-semibold">
                Personalised for {form.organization.trim()}
              </p>
            </div>
            <a href={link} target="_blank" rel="noopener noreferrer"
              className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-xl
                bg-[#0056A8] hover:bg-[#003366] px-6 py-4 text-sm font-bold uppercase
                tracking-wider text-white transition-colors"
              data-testid="onepager-download-link">
              <ExternalLink className="w-4 h-4" /> Open the PDF again
            </a>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3 pt-1" data-testid="onepager-form">
            <input className={field} placeholder="Full name *" value={form.name}
              onChange={(e) => set("name", e.target.value)} data-testid="onepager-field-name" />
            <input className={field} placeholder="Health system / hospital *"
              value={form.organization} onChange={(e) => set("organization", e.target.value)}
              data-testid="onepager-field-org" />
            <div className="grid sm:grid-cols-2 gap-3">
              <input className={field} type="email" placeholder="Work email *" value={form.email}
                onChange={(e) => set("email", e.target.value)} data-testid="onepager-field-email" />
              <select className={field} value={form.title}
                onChange={(e) => set("title", e.target.value)} data-testid="onepager-field-role">
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <input className={field} placeholder="Phone (optional)" value={form.phone}
              onChange={(e) => set("phone", e.target.value)} data-testid="onepager-field-phone" />
            <button type="submit" disabled={sending}
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl
                bg-gradient-to-r from-[#0b2545] to-[#13395f] px-6 py-4 text-[15px] font-black
                uppercase tracking-wider text-white transition-all hover:brightness-110
                disabled:opacity-60"
              data-testid="onepager-submit">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
              Send me the one-pager
            </button>
            <p className="text-[11px] text-slate-500 text-center">
              We use this to personalise the document and follow up once. No lists, no drip.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default HartOnePagerDialog;
